import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { CertificateResource } from "@octopusdeploy/octopus-server-client";
import { SelfSignedCertificateCurve } from "@octopusdeploy/octopus-server-client";
import * as _ from "lodash";
import * as React from "react";
import { ExpandableFormSection, FormSectionHeading, Note, Select, Sensitive, SensitiveFileUpload, Summary, Text } from "~/components/form/index";
interface NewCertificateDetailsFormSectionsProps {
    model: CertificateResource;
    setModelState: <K extends keyof CertificateResource>(state: Pick<CertificateResource, K>) => void;
    getFieldError: (fieldName: string) => string;
    setUploadFormat: (uploadFormat: UploadFormatOptions) => void;
    uploadFormat: UploadFormatOptions;
}
type UploadFormatOptions = "File" | "Text" | "SelfSigned";
export function NewCertificateDetailsFormSections(props: NewCertificateDetailsFormSectionsProps) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const nonNullCertificateDataNewValue = props.model.CertificateData.NewValue!;
    return (<div>
            <FormSectionHeading title="Certificate"/>
            <ExpandableFormSection errorKey="CertificateData" title="Certificate Data" summary={Summary.summary("Supported formats: PFX (PKCS #12), DER, PEM")} help="Select the option which you wish to use to supply the Certificate Data">
                <RadioButtonGroup value={props.uploadFormat} onChange={(uploadFormat: UploadFormatOptions) => props.setUploadFormat(uploadFormat)}>
                    <RadioButton value={"File"} label="Upload a File"/>
                    {props.uploadFormat === "File" && (<SensitiveFileUpload label="Certificate File" value={props.model.CertificateData} onChange={(CertificateData) => props.setModelState({ CertificateData })} error={props.getFieldError("CertificateData")}/>)}

                    <RadioButton value={"Text"} label="Paste Text"/>
                    {props.uploadFormat === "Text" && (<div>
                            <Note>Paste your certificate as text, either base64 encoded or in PEM format</Note>
                            <Text multiline={true} value={nonNullCertificateDataNewValue} onChange={(CertificateData) => props.setModelState({ CertificateData: { NewValue: _.trim(CertificateData), HasValue: true } })}/>
                        </div>)}

                    <RadioButton value={"SelfSigned"} label="Create Self Signed"/>
                    {props.uploadFormat === "SelfSigned" && (<>
                            <div>
                                <Note>Create a new self signed certificate with the details below.</Note>
                                <Text label={"Common Name (a FQDN or person's name)"} value={props.model.SubjectCommonName || ""} onChange={(SubjectCommonName) => props.setModelState({ SubjectCommonName })}/>
                            </div>
                            <div>
                                <Select value={props.model.SelfSignedCertificateCurve} onChange={(x) => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                props.setModelState({ SelfSignedCertificateCurve: x as SelfSignedCertificateCurve });
            }} items={[
                { value: SelfSignedCertificateCurve.nistP256, text: "NIST P-256" },
                { value: SelfSignedCertificateCurve.nistP384, text: "NIST P-384" },
                { value: SelfSignedCertificateCurve.nistP521, text: "NIST P-521" },
            ]} placeholder="Elliptic curve to use"/>
                                <Note>
                                    If this is left blank, P-384 will be used by default.
                                    <br />
                                    If you want to maximize interoperability with existing browsers and servers, select either P-256 and P-384
                                </Note>
                                {props.model.SelfSignedCertificateCurve === SelfSignedCertificateCurve.nistP521 && <Callout type={"warning"} title={"Using anything other than NIST P-256 or NIST P-384 will cause some browsers to block access."}/>}
                            </div>
                        </>)}
                </RadioButtonGroup>
            </ExpandableFormSection>
            {props.uploadFormat !== "SelfSigned" && (<ExpandableFormSection errorKey="Password" title="Password" summary={Summary.summary("The password protecting the file (if required).")} help="Password">
                    <Sensitive value={props.model.Password} onChange={(Password) => props.setModelState({ Password })} label="Password" error={props.getFieldError("Password")}/>
                </ExpandableFormSection>)}
        </div>);
}
